import React, { useEffect, useState, useRef } from 'react';
import s from './LoadingScreen.module.css';

function AnimatedDots() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : d + '.'));
    }, 420);
    return () => clearInterval(id);
  }, []);
  return <span className={s.progressDots}>{dots}</span>;
}

const DESKTOP_HERO = [
  '/hero-images/zohar-25.webp',
  '/hero-images/zohar-24.webp',
  '/hero-images/zohar-23.webp',
  '/hero-images/sivan-22.webp',
  '/hero-images/zohar-17.webp',
];

const MOBILE_HERO = [
  '/hero-images/zohar-kitchen.webp',
  '/hero-images/gal-13.webp',
  '/hero-images/gila-dining.webp',
  '/hero-images/hila-pool.webp',
  '/hero-images/gal-living.webp',
];

const MIN_DISPLAY_MS = 1100;
const FADE_MS = 720;

export default function LoadingScreen() {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [gone, setGone] = useState(false);
  const mountedAt = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());

  useEffect(() => {
    let cancelled = false;
    const list = (typeof window !== 'undefined' && window.innerWidth <= 768) ? MOBILE_HERO : DESKTOP_HERO;
    const total = list.length;
    let loaded = 0;

    const bump = () => {
      if (cancelled) return;
      loaded += 1;
      setProgress((loaded / total) * 100);
      if (loaded >= total) {
        const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - mountedAt.current;
        const wait = Math.max(0, MIN_DISPLAY_MS - elapsed);
        setTimeout(() => {
          if (cancelled) return;
          setProgress(100);
          setDone(true);
          setTimeout(() => !cancelled && setGone(true), FADE_MS);
        }, wait);
      }
    };

    // Smooth progress visual even if images resolve from cache instantly
    const fake = setInterval(() => {
      if (cancelled || loaded >= total) return;
      setProgress((p) => {
        const target = (loaded / total) * 100;
        const next = Math.min(target - 2, p + 1.2);
        return next > p ? next : p;
      });
    }, 70);

    list.forEach((src) => {
      const img = new Image();
      img.onload = bump;
      img.onerror = bump;
      img.src = src;
    });

    // Absolute safety fallback — never lock the page
    const safetyTimer = setTimeout(() => {
      if (cancelled) return;
      setProgress(100);
      setDone(true);
      setTimeout(() => !cancelled && setGone(true), FADE_MS);
    }, 7000);

    return () => {
      cancelled = true;
      clearInterval(fake);
      clearTimeout(safetyTimer);
    };
  }, []);

  // Lock body scroll while loading
  useEffect(() => {
    if (gone) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [gone]);

  if (gone) return null;

  const pct = Math.min(100, Math.round(progress));

  return (
    <div
      className={`${s.overlay} ${done ? s.overlayDone : ''}`}
      role="status"
      aria-live="polite"
      aria-label={`טוען את האתר ${pct}%`}
    >
      {/* Architectural corner marks */}
      <span className={s.corner} data-corner="tl" />
      <span className={s.corner} data-corner="tr" />
      <span className={s.corner} data-corner="bl" />
      <span className={s.corner} data-corner="br" />

      <div className={s.inner}>
        <div className={s.brand}>
          <span className={s.mastheadLine} />
          <h1 className={s.brandText}>
            <span className={s.brandName}>LANGER</span>
            <span className={s.brandHE}>אדריכלות ועיצוב פנים</span>
          </h1>
          <span className={s.mastheadLine} />
        </div>

        <div className={s.progressWrap}>
          <div className={s.progressTrack}>
            <div className={s.progressFill} style={{ width: `${pct}%` }} />
          </div>
          <div className={s.progressRow}>
            <span className={s.progressLabel}>טוענים את החוויה<AnimatedDots /></span>
            <span className={s.progressPct}>
              <span className={s.pctNum}>{String(pct).padStart(3, '\u00A0')}</span>
              <span className={s.pctSign}>%</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
